import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdDragIndicator } from 'react-icons/md';
import { FiArrowDown, FiArrowUp, FiFolder, FiTrash2 } from 'react-icons/fi';
import Sortable from 'sortablejs';
import { v4 as uuid } from 'uuid';
import Modal from '../../Modal';
import Btn from '../../Buttons/Btn';
import {
  ItemTarget, MenuCatalog, MenuNode, isCustomItem, layoutFile, moveNode, parseLayout, reconcileLayout
} from './layout';
import { Editor } from './styles';
import IconPicker from './IconPicker';
import { ItemIcon } from './itemIcons';

type Props = {
  nodes: MenuNode[];
  defaults: MenuNode[];
  catalog: MenuCatalog;
  onSave: (nodes: MenuNode[], reset: boolean) => void;
  onClose: () => void;
};

export default function MenuCustomization({ nodes, defaults, catalog, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(nodes);
  const [itemName, setItemName] = useState('');
  const [itemIcon, setItemIcon] = useState<ItemIcon>('folder');
  const [reset, setReset] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [error, setError] = useState('');
  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const update = useCallback((next: MenuNode[]) => { setDraft(next); setReset(false); setError(''); }, []);

  useEffect(() => {
    setDraft(current => reconcileLayout(current, defaults, catalog));
  }, [defaults, catalog]);

  // Sortable moves DOM nodes; restore the original DOM before React applies the new tree.
  useEffect(() => {
    const lists = editorRef.current?.querySelectorAll<HTMLElement>('.menu-list');
    const instances = Array.from(lists || []).map(list => Sortable.create(list, {
      group: 'smartsell-menu-editor',
      animation: 150,
      handle: '.menu-handle',
      draggable: '.menu-node',
      fallbackOnBody: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 20,
      onEnd: event => {
        const { item, from, to, oldDraggableIndex, newDraggableIndex } = event;
        const id = item.dataset.menuId;
        const parent = to.dataset.parentId || '';
        item.remove();
        const siblings = Array.from(from.children).filter(child => child.classList.contains('menu-node'));
        from.insertBefore(item, siblings[oldDraggableIndex ?? 0] || null);
        if (id) {
          const next = moveNode(draft, id, parent, newDraggableIndex ?? 0);
          if (next === draft && (from !== to || oldDraggableIndex !== newDraggableIndex)) {
            setError(t('menuCustomization.depthLimit'));
          } else update(next);
        }
      }
    }));
    return () => instances.forEach(instance => instance.destroy());
  }, [draft, t, update]);

  const label = (node: MenuNode) => isCustomItem(node) ? node.label || '' : t(catalog.get(node.id)?.title || '');
  const pages: { id: string; label: string }[] = [];
  const collectPages = (list: MenuNode[], ancestors: string[] = []) => list.forEach(node => {
    const item = catalog.get(node.id);
    if (!item) return;
    const names = [...ancestors, t(item.title)];
    if (item.path && !item.sidebarSubItems) pages.push({ id: node.id, label: `${names.join(' → ')} (${item.path})` });
    if (node.children) collectPages(node.children, names);
  });
  collectPages(defaults);
  const editNode = (list: MenuNode[], id: string, change: Partial<MenuNode>): MenuNode[] => list.map(node => ({
    ...node, ...(node.id === id ? change : {}),
    ...(node.children ? { children: editNode(node.children, id, change) } : {})
  }));
  const removed: MenuNode[] = [];
  const collectRemoved = (list: MenuNode[]) => list.forEach(node => {
    if (node.hidden) removed.push(node);
    else if (node.children) collectRemoved(node.children);
  });
  collectRemoved(draft);
  const move = (id: string, parent: string, index: number) => {
    const next = moveNode(draft, id, parent, index);
    if (next === draft) setError(t('menuCustomization.depthLimit'));
    else update(next);
  };

  const renderList = (list: MenuNode[], parent = '') => (
    <div className="menu-list" data-parent-id={parent}>
      {list.filter(node => !node.hidden).map((node, index, visible) => {
        const custom = isCustomItem(node);
        if (!custom && !catalog.has(node.id)) return null;
        const Icon = node.children ? FiFolder : catalog.get(node.id)?.icon;
        return (
          <div className="menu-node" key={node.id} data-menu-id={node.id}>
            <div className="menu-row">
              <button type="button" className="menu-handle" aria-label={t('menuCustomization.drag', { name: label(node) })}>
                <MdDragIndicator size={22} />
              </button>
              {custom ? (
                <IconPicker value={node.icon || 'folder'}
                  onChange={icon => update(editNode(draft, node.id, { icon }))} />
              ) : Icon && <Icon />}
              <div className="menu-content">
                {custom ? (
                  <input
                    className="menu-name" aria-label={t('menuCustomization.itemName')} maxLength={80}
                    value={node.label} onChange={e => update(editNode(draft, node.id, { label: e.target.value }))}
                  />
                ) : <span className="menu-name">{label(node)}</span>}
                {custom ? (
                  <div className="menu-target">
                    <span>{t('menuCustomization.action')}</span>
                    <select aria-label={t('menuCustomization.targetType')}
                      value={node.target?.type || 'screen'}
                      onChange={e => {
                        const target: ItemTarget | undefined = e.target.value === 'url'
                          ? { type: 'url', url: '' } : undefined;
                        update(editNode(draft, node.id, { target }));
                      }}>
                      <option value="screen">{t('menuCustomization.pages')}</option>
                      <option value="url">{t('menuCustomization.url')}</option>
                    </select>
                    {node.target?.type === 'url' ? (
                      <input type="url" aria-label={t('menuCustomization.url')} placeholder="https://"
                        maxLength={2048} value={node.target.url}
                        onChange={e => update(editNode(draft, node.id, {
                          target: { type: 'url', url: e.target.value }
                        }))} />
                    ) : (
                      <select aria-label={t('menuCustomization.page')} value={node.target?.menuId || ''}
                        onChange={e => update(editNode(draft, node.id, {
                          target: e.target.value ? { type: 'screen', menuId: e.target.value } : undefined
                        }))}>
                        <option value="">{t('menuCustomization.selectPage')}</option>
                        {pages.map(page => <option key={page.id} value={page.id}>{page.label}</option>)}
                      </select>
                    )}
                    {node.children?.some(child => !child.hidden) && (
                      <small>{t('menuCustomization.childrenAction')}</small>
                    )}
                  </div>
                ) : (
                  <div className="menu-target menu-target-readonly">
                    <span className="menu-target-type">
                      {t(catalog.get(node.id)?.path ? 'menuCustomization.pages' : 'menuCustomization.subitems')}
                    </span>
                    <span className="menu-target-destination">
                      {catalog.get(node.id)?.path
                        ? pages.find(page => page.id === node.id)?.label || catalog.get(node.id)?.path
                        : t('menuCustomization.openSubitems')}
                    </span>
                  </div>
                )}
              </div>
              <div className="menu-actions">
                <button type="button" title={t('menuCustomization.up')} aria-label={t('menuCustomization.up')}
                  disabled={index === 0} onClick={() => move(node.id, parent, index - 1)}><FiArrowUp /></button>
                <button type="button" title={t('menuCustomization.down')} aria-label={t('menuCustomization.down')}
                  disabled={index === visible.length - 1}
                  onClick={() => move(node.id, parent, index + 1)}><FiArrowDown /></button>
                <button type="button" title={t('menuCustomization.removeItem')}
                  aria-label={t('menuCustomization.removeItem')}
                  onClick={() => update(editNode(draft, node.id, { hidden: true }))}><FiTrash2 /></button>
              </div>
            </div>
            {node.children && renderList(node.children, node.id)}
          </div>
        );
      })}
      {parent && list.every(node => node.hidden) && <div className="menu-empty">{t('menuCustomization.dropHere')}</div>}
    </div>
  );

  const validateDraft = () => parseLayout(layoutFile(reconcileLayout(draft, defaults, catalog))).items;
  const exportFile = () => {
    try {
      const blob = new Blob([JSON.stringify(layoutFile(validateDraft()), null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'smartsell-menu.json';
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setError('');
    } catch { setError(t('menuCustomization.invalidItem')); }
  };
  const importFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      if (file.size > 256 * 1024) throw new Error('size');
      const imported = parseLayout(JSON.parse(await file.text()));
      update(reconcileLayout(imported.items, defaults, catalog));
    } catch { setError(t('menuCustomization.invalidFile')); }
  };
  const save = () => {
    try {
      const next = validateDraft();
      onSave(next, reset);
      onClose();
    } catch (error) {
      setError(t(error instanceof Error && error.message === 'invalidMenuLayout'
        ? 'menuCustomization.invalidItem' : 'menuCustomization.storageError'));
    }
  };

  const restoreDefaults = () => {
    setDraft(defaults);
    setReset(true);
    setError('');
    setShowResetConfirmation(false);
  };

  return (
    <>
      <Modal title={t('menuCustomization.title')} showModal closeModal={onClose} cancelButton={onClose}
        action={save} style={{ width: '960px' }}>
        <Editor ref={editorRef}>
          <p className="menu-help">{t('menuCustomization.help')}</p>
          <div className="menu-toolbar">
            <IconPicker value={itemIcon} onChange={setItemIcon} />
            <input aria-label={t('menuCustomization.itemName')} placeholder={t('menuCustomization.itemName')}
              value={itemName} maxLength={80} onChange={e => setItemName(e.target.value)} />
            <Btn text={t('menuCustomization.addItem')} disabled={!itemName.trim()}
              onClick={() => {
                update([{ id: `item:${uuid()}`, label: itemName.trim(), icon: itemIcon, children: [] }, ...draft]);
                editorRef.current?.parentElement?.scrollTo({ top: 0 });
                setItemName('');
              }} />
          </div>
          <div className="menu-toolbar">
            <Btn type="outlined" text={t('menuCustomization.reset')}
              onClick={() => setShowResetConfirmation(true)} />
            <Btn type="outlined" text={t('menuCustomization.export')} onClick={exportFile} />
            <Btn type="outlined" text={t('menuCustomization.import')} onClick={() => fileRef.current?.click()} />
            <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={importFile} />
          </div>
          {error && <div className="menu-error" role="alert">{error}</div>}
          {renderList(draft)}
          {removed.length > 0 && (
            <details className="menu-removed">
              <summary>{t('menuCustomization.removedItems', { count: removed.length })}</summary>
              <p>{t('menuCustomization.removedHelp')}</p>
              {removed.map(node => (
                <div className="menu-removed-row" key={node.id}>
                  <span>{label(node)}</span>
                  <Btn type="text" text={t('menuCustomization.restoreItem')}
                    onClick={() => update(editNode(draft, node.id, { hidden: false }))} />
                </div>
              ))}
            </details>
          )}
        </Editor>
      </Modal>
      <Modal
        title={t('confirmResetTitle')}
        showModal={showResetConfirmation}
        closeModal={() => setShowResetConfirmation(false)}
        cancelButton={() => setShowResetConfirmation(false)}
        action={restoreDefaults}
        actionNameBtn={t('confirmReset')}
        style={{ width: '460px' }}
      >
        <p style={{padding: '1rem'}}>{t('confirmResetMessage')}</p>
      </Modal>
    </>
  );
}
